"use client"

/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Reusable pricing block — renders the plan catalogue from the API
 *   PROPS:   ctaHref (link mode) XOR onSelect (callback mode), ctaLabel?, showHeader?, className?
 *   STATE:   delegates data fetching to usePricing()
 *   DEPENDS: M-UI-CARD, M-UI-BUTTON, M-UI-BADGE, M-USE-PRICING, next-intl, next/link
 *   EMITS:   onSelect(planCode) when a plan is chosen (checkout mode)
 *   ACCESSIBILITY: cards are region landmarks; CTAs are focusable buttons/links, min 44px
 *   LINKS:   M-FEATURE-PRICING-PLANS
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-16] Initial reusable pricing component, DB-driven, used on landing + dashboard + checkout
 */

import Link from "next/link"
import { useLocale, useTranslations } from "next-intl"
import { CheckCircle, Sparkle } from "@phosphor-icons/react/dist/ssr"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { usePricing, type Plan } from "@/hooks/use-pricing"

interface PricingPlansProps {
  ctaHref?: string
  onSelect?: (planCode: string) => void
  ctaLabel?: string
  showHeader?: boolean
  className?: string
}

function formatPrice(plan: Plan, locale: string): string {
  const amount = Number(plan.price_amount)
  const formatted = new Intl.NumberFormat(locale === "ru" ? "ru-RU" : "en-US").format(amount)
  const symbol = plan.price_currency === "RUB" ? "₽" : plan.price_currency
  return `${formatted} ${symbol}`
}

export function PricingPlans({
  ctaHref,
  onSelect,
  ctaLabel,
  showHeader = true,
  className = "",
}: PricingPlansProps) {
  const tpr = useTranslations("pricing_new")
  const tc = useTranslations("cta_new")
  const locale = useLocale()
  const { plans, loading, error } = usePricing()

  if (loading) {
    return (
      <div className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${className}`}>
        {[0, 1, 2].map((i) => (
          <Card key={i} variant="default" className="animate-fade-in">
            <CardContent className="p-6">
              <div className="mb-2 h-6 w-1/3 rounded-sm bg-surface-elevated-dark" />
              <div className="mb-6 h-9 w-2/3 rounded-sm bg-surface-elevated-dark" />
              <div className="mb-2 h-4 w-full rounded-sm bg-surface-elevated-dark" />
              <div className="mb-6 h-4 w-3/4 rounded-sm bg-surface-elevated-dark" />
              <div className="h-10 w-full rounded-md bg-surface-elevated-dark" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error || plans.length === 0) {
    return (
      <p className="text-center text-sm text-muted">
        {error ?? tpr("empty")}
      </p>
    )
  }

  const buttonLabel = ctaLabel ?? tc("button")

  return (
    <div className={showHeader ? "" : className}>
      {showHeader && (
        <div className={`mb-12 text-center ${className}`}>
          <div className="mb-4">
            <Badge variant="warning">{tpr("badge")}</Badge>
          </div>
          <h2 className="text-3xl text-on-dark md:text-display-md">{tpr("title")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted">{tpr("desc")}</p>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${showHeader ? "" : className}`}>
        {plans.map((plan) => (
          <Card
            key={plan.code}
            variant={plan.is_highlighted ? "promo" : "default"}
            className={plan.is_highlighted ? "border-primary/30" : ""}
          >
            <CardContent className="p-6">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-on-dark">{plan.name}</h3>
                {plan.is_highlighted && (
                  <Badge variant="success">
                    <Sparkle weight="fill" className="h-3 w-3" aria-hidden="true" />
                    {tpr("pro_badge")}
                  </Badge>
                )}
              </div>
              <p className="mb-1 font-number text-display-md text-primary">{formatPrice(plan, locale)}</p>
              <p className="mb-4 text-xs text-muted">{tpr("per_month")}</p>
              {plan.description && <p className="mb-4 text-sm text-muted">{plan.description}</p>}
              <ul className="mb-6 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-muted">
                    <CheckCircle weight="fill" className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              {onSelect ? (
                <Button
                  variant={plan.is_highlighted ? "primary" : "secondary"}
                  className="w-full"
                  onClick={() => onSelect(plan.code)}
                >
                  {buttonLabel}
                </Button>
              ) : ctaHref ? (
                <Link href={ctaHref}>
                  <Button variant={plan.is_highlighted ? "primary" : "secondary"} className="w-full">
                    {buttonLabel}
                  </Button>
                </Link>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
